/**
 * Synchronise Shoptimizer's body-class drawers with accessible control state.
 * The parent theme remains responsible for its normal open/close behaviour.
 */
(() => {
  'use strict';

  const selectors = {
    account: '.shoptimizer-myaccount a',
    cart: '.site-header-cart .cart-contents, .shoptimizer-cart-shortcode .cart-contents',
    cartDrawer: '#shoptimizerCartDrawer',
    desktopSearch: '.search-trigger[data-trigger="searchToggle"]',
    filter: '.mobile-filter.shoptimizer-mobile-toggle, .shoptimizer-mobile-filter',
    filterDrawer: '.secondary-wrapper',
    menu: '.menu-toggle',
    menuClose: '.mobile-menu.close-drawer',
    menuDrawer: '.col-full-nav',
    mobileSearch: '.mobile-search-toggle',
    mobileSearchDrawer: '.site-header .site-search',
    searchDialog: 'dialog[data-shoptimizermodal-id="searchToggle"]',
  };
  const classes = {
    cart: 'drawer-open',
    filter: 'filter-open',
    menu: 'mobile-toggled',
    mobileSearch: 'm-search-toggled',
  };
  const isGerman = document.documentElement.lang.toLowerCase().startsWith('de');
  const isDutch = document.documentElement.lang.toLowerCase().startsWith('nl');
  const labels = isGerman
    ? {
      account: 'Mein Konto',
      cart: 'Warenkorb ansehen',
      cartClose: 'Warenkorb schließen',
      cartItemPlural: 'Artikel',
      cartItemSingular: 'Artikel',
      filterClose: 'Filter schließen',
      filterOpen: 'Filter öffnen',
      menuClose: 'Menü schließen',
      menuOpen: 'Menü öffnen',
      paginationNext: 'Nächste Seite',
      paginationPrevious: 'Vorherige Seite',
      submenuClose: 'Untermenü schließen',
      submenuOpen: 'Untermenü öffnen',
      search: 'Suche',
      searchClose: 'Suche schließen',
      searchOpen: 'Suche öffnen',
      quantityDecrease: 'Menge verringern',
      quantityIncrease: 'Menge erhöhen',
    }
    : isDutch
      ? {
        account: 'Mijn account',
        cart: 'Winkelwagen bekijken',
        cartClose: 'Winkelwagen sluiten',
        cartItemPlural: 'artikelen',
        cartItemSingular: 'artikel',
        filterClose: 'Filters sluiten',
        filterOpen: 'Filters openen',
        menuClose: 'Menu sluiten',
        menuOpen: 'Menu openen',
        paginationNext: 'Volgende pagina',
        paginationPrevious: 'Vorige pagina',
        submenuClose: 'Submenu sluiten',
        submenuOpen: 'Submenu openen',
        search: 'Zoeken',
        searchClose: 'Zoeken sluiten',
        searchOpen: 'Zoeken openen',
        quantityDecrease: 'Aantal verlagen',
        quantityIncrease: 'Aantal verhogen',
      }
      : {
        account: 'My account',
        cart: 'View cart',
        cartClose: 'Close cart',
        cartItemPlural: 'items',
        cartItemSingular: 'item',
        filterClose: 'Close filters',
        filterOpen: 'Open filters',
        menuClose: 'Close menu',
        menuOpen: 'Open menu',
        paginationNext: 'Next page',
        paginationPrevious: 'Previous page',
        submenuClose: 'Close submenu',
        submenuOpen: 'Open submenu',
        search: 'Search',
        searchClose: 'Close search',
        searchOpen: 'Open search',
        quantityDecrease: 'Decrease quantity',
        quantityIncrease: 'Increase quantity',
      };
  const previous = {};
  const lastTriggers = {};
  let lastExternalFocus = null;

  const closest = (target, selector) => (
    target && 'function' === typeof target.closest ? target.closest(selector) : null
  );

  const ensureId = (element, id) => {
    if (element && !element.id) {
      element.id = id;
    }

    return element?.id;
  };

  const setControls = (selector, target, fallbackId) => {
    const targetId = ensureId(target, fallbackId);

    if (targetId) {
      document.querySelectorAll(selector).forEach((control) => {
        control.setAttribute('aria-controls', targetId);
      });
    }
  };

  const isNativeModalDialog = (dialog) => {
    if (!dialog?.open) {
      return false;
    }

    try {
      return dialog.matches(':modal');
    } catch {
      return 'true' === dialog.dataset.fvNativeModal;
    }
  };

  /**
   * The parent opens search with dialog.show(), which leaves the page behind it
   * interactive. Reopen the already-visible dialog in the top layer after the
   * parent's click handler has run, preserving the input it focused.
   */
  const promoteSearchDialog = () => {
    const dialog = document.querySelector(selectors.searchDialog);

    if (
      !dialog?.open
      || 'function' !== typeof dialog.showModal
      || isNativeModalDialog(dialog)
    ) {
      return;
    }

    const focusedElement = dialog.contains(document.activeElement)
      ? document.activeElement
      : null;

    dialog.dataset.fvPromotingModal = 'true';
    dialog.close();

    try {
      dialog.showModal();
      dialog.dataset.fvNativeModal = 'true';
    } catch {
      delete dialog.dataset.fvNativeModal;

      // Preserve the parent's working non-modal state if native promotion fails.
      if (!dialog.open && 'function' === typeof dialog.show) {
        dialog.show();
      }
    } finally {
      delete dialog.dataset.fvPromotingModal;
    }

    if (
      dialog.open
      && focusedElement?.isConnected
      && dialog.contains(focusedElement)
    ) {
      focusedElement.focus({ preventScroll: true });
    }
  };

  const focusReturnTarget = (type) => {
    const candidates = [
      lastTriggers[type],
      lastExternalFocus,
      ...document.querySelectorAll(selectors[type]),
    ];
    const target = candidates.find((candidate) => (
      candidate
      && candidate.isConnected
      && 'function' === typeof candidate.focus
      && !closest(candidate, '[inert]')
    ));

    target?.focus({ preventScroll: true });
  };

  const setDrawerAvailability = (drawer, isUnavailable, type) => {
    if (!drawer) {
      return;
    }

    if (!isUnavailable) {
      drawer.removeAttribute('aria-hidden');
      drawer.removeAttribute('inert');
      return;
    }

    if (document.activeElement && drawer.contains(document.activeElement)) {
      focusReturnTarget(type);
    }

    drawer.setAttribute('aria-hidden', 'true');
    drawer.setAttribute('inert', '');
  };

  const updateToggles = (selector, isOpen, openLabel, closeLabel) => {
    document.querySelectorAll(selector).forEach((toggle) => {
      toggle.setAttribute('aria-expanded', String(isOpen));
      toggle.setAttribute('aria-label', isOpen ? closeLabel : openLabel);
    });
  };

  const submenuItemLabel = (item) => (
    item
      ?.querySelector(':scope > a, :scope > span:not(.caret)')
      ?.textContent
      ?.replace(/\s+/g, ' ')
      .trim()
    || ''
  );

  const syncSubmenuToggles = () => {
    document
      .querySelectorAll('.main-navigation ul.menu li.menu-item-has-children > span.caret')
      .forEach((toggle) => {
        const item = toggle.parentElement;
        const isOpen = Boolean(item?.classList.contains('dropdown-open'));
        const itemLabel = submenuItemLabel(item);
        const actionLabel = isOpen ? labels.submenuClose : labels.submenuOpen;

        toggle.setAttribute('aria-expanded', String(isOpen));
        toggle.setAttribute(
          'aria-label',
          itemLabel ? `${actionLabel}: ${itemLabel}` : actionLabel
        );

        if (window.innerWidth <= 992) {
          item?.setAttribute('aria-expanded', String(isOpen));
        }
      });
  };

  const decorateSubmenuToggles = () => {
    document
      .querySelectorAll('.main-navigation ul.menu li.menu-item-has-children > span.caret')
      .forEach((toggle, index) => {
        const item = toggle.parentElement;
        const submenu = item?.querySelector(':scope > .sub-menu-wrapper, :scope > ul.sub-menu');
        const submenuId = ensureId(submenu, `fv-mobile-submenu-${index + 1}`);

        toggle.setAttribute('role', 'button');
        toggle.setAttribute('tabindex', '0');

        if (submenuId) {
          toggle.setAttribute('aria-controls', submenuId);
        }

        if ('true' !== toggle.dataset.fvKeyboardReady) {
          toggle.dataset.fvKeyboardReady = 'true';
          toggle.addEventListener('keydown', (event) => {
            if ('Enter' !== event.key && ' ' !== event.key) {
              return;
            }

            event.preventDefault();
            toggle.click();
            window.setTimeout(syncSubmenuToggles, 0);
          });
        }
      });

    syncSubmenuToggles();
  };

  const getCartLabel = (link) => {
    const rawCount = link.querySelector('.mini-count, .count')?.textContent?.trim();
    const count = Number((rawCount || '').replace(',', '.'));
    const amount = link.querySelector('.amount')?.textContent?.trim();
    const details = [];

    if (Number.isFinite(count)) {
      const itemLabel = 1 === count
        ? labels.cartItemSingular
        : labels.cartItemPlural;
      details.push(`${rawCount} ${itemLabel}`);
    }

    if (amount) {
      details.push(amount);
    }

    return [labels.cart, ...details].join(', ');
  };

  const decorateControls = () => {
    const navigation = document.querySelector('#site-navigation');
    const menuDrawer = document.querySelector(selectors.menuDrawer);
    const filterDrawer = document.querySelector(selectors.filterDrawer);
    const mobileSearchDrawer = document.querySelector(selectors.mobileSearchDrawer);
    const cartDrawer = document.querySelector(selectors.cartDrawer);
    const searchDialog = document.querySelector(selectors.searchDialog);

    setControls(selectors.menu, navigation || menuDrawer, 'fv-mobile-navigation');
    setControls(selectors.filter, filterDrawer, 'fv-product-filters');
    setControls(selectors.mobileSearch, mobileSearchDrawer, 'fv-mobile-search');
    setControls(selectors.cart, cartDrawer, 'shoptimizerCartDrawer');
    setControls(selectors.desktopSearch, searchDialog, 'fv-search-dialog');

    document.querySelectorAll(selectors.account).forEach((link) => {
      link.setAttribute('aria-label', labels.account);
      link.setAttribute('title', labels.account);
    });

    // Keep normal cart destinations as links; expose the parent's href="#" drawer trigger as a button.
    document.querySelectorAll(selectors.cart).forEach((link) => {
      const accessibleLabel = getCartLabel(link);
      const rawHref = link.getAttribute('href');
      const opensDrawer = Boolean(cartDrawer && (!rawHref || '#' === rawHref));

      if (opensDrawer) {
        link.setAttribute('role', 'button');

        if ('true' !== link.dataset.fvKeyboardReady) {
          link.dataset.fvKeyboardReady = 'true';
          link.addEventListener('keydown', (event) => {
            if (' ' === event.key) {
              event.preventDefault();
              link.click();
            }
          });
        }
      } else {
        link.removeAttribute('role');
      }

      link.setAttribute('aria-label', accessibleLabel);
      link.setAttribute('title', accessibleLabel);
    });

    document.querySelectorAll('.mobile-menu.close-drawer').forEach((button) => {
      button.setAttribute('type', 'button');
      button.setAttribute('aria-label', labels.menuClose);
    });

    decorateSubmenuToggles();

    document.querySelectorAll('.quantity-button').forEach((button) => {
      const isIncrease = button.classList.contains('quantity-up');
      const isDecrease = button.classList.contains('quantity-down');

      if (!isIncrease && !isDecrease) {
        return;
      }

      button.setAttribute(
        'aria-label',
        isIncrease ? labels.quantityIncrease : labels.quantityDecrease
      );

      if ('BUTTON' !== button.tagName) {
        button.setAttribute('role', 'button');
        button.setAttribute('tabindex', '0');
      }

      if ('true' !== button.dataset.fvKeyboardReady) {
        button.dataset.fvKeyboardReady = 'true';
        button.addEventListener('keydown', (event) => {
          if ('Enter' !== event.key && ' ' !== event.key) {
            return;
          }

          event.preventDefault();
          button.click();
        });
      }
    });

    document.querySelectorAll(`${selectors.filterDrawer} .filters.close-drawer`).forEach((button) => {
      button.setAttribute('aria-label', labels.filterClose);

      if ('BUTTON' !== button.tagName) {
        button.setAttribute('role', 'button');
        button.setAttribute('tabindex', '0');
      }
    });

    cartDrawer?.querySelectorAll('.close-drawer').forEach((button) => {
      button.setAttribute('aria-label', labels.cartClose);
    });

    if (searchDialog) {
      const heading = searchDialog.querySelector('[role="heading"]');
      const closeButton = searchDialog.querySelector('.shoptimizer-modal--button_close');

      searchDialog.setAttribute('aria-label', labels.search);
      closeButton?.setAttribute('aria-label', labels.searchClose);
      heading?.setAttribute('aria-level', '2');

      if ('true' !== searchDialog.dataset.fvHeaderBound) {
        searchDialog.dataset.fvHeaderBound = 'true';
        searchDialog.addEventListener('close', () => {
          // Ignore the intermediate close used to promote show() to showModal().
          if ('true' === searchDialog.dataset.fvPromotingModal || searchDialog.open) {
            return;
          }

          delete searchDialog.dataset.fvNativeModal;
          syncState();
          focusReturnTarget('desktopSearch');
        });
      }
    }

    document
      .querySelectorAll('nav.pagination a.page-numbers, nav.woocommerce-pagination a.page-numbers')
      .forEach((link) => {
        const isNext = link.classList.contains('next');
        const isPrevious = link.classList.contains('prev');

        if (!isNext && !isPrevious) {
          return;
        }

        link.setAttribute(
          'aria-label',
          isNext ? labels.paginationNext : labels.paginationPrevious
        );

        if (!link.textContent.trim()) {
          const arrow = document.createElement('span');
          arrow.setAttribute('aria-hidden', 'true');
          arrow.textContent = isNext ? '→' : '←';
          link.appendChild(arrow);
        }
      });
  };

  const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[contenteditable="true"]',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  const isVisiblyFocusable = (element) => {
    const submenu = element?.closest('.sub-menu-wrapper');

    if (
      !element
      || element.matches('[disabled], [aria-disabled="true"]')
      || element.tabIndex < 0
      || closest(element, '[inert], [aria-hidden="true"]')
      || (
        window.innerWidth <= 992
        && submenu
        && !submenu.parentElement?.classList.contains('dropdown-open')
      )
    ) {
      return false;
    }

    const style = window.getComputedStyle(element);

    return (
      'none' !== style.display
      && 'hidden' !== style.visibility
      && element.getClientRects().length > 0
    );
  };

  const drawerFocusables = (type) => {
    const drawer = document.querySelector(selectors[`${type}Drawer`]);
    const closeControl = 'menu' === type
      ? document.querySelector(selectors.menuClose)
      : drawer?.querySelector('.filters.close-drawer');
    const drawerControls = drawer
      ? [...drawer.querySelectorAll(focusableSelector)]
      : [];
    const candidates = 'menu' === type
      ? [closeControl, ...drawerControls]
      : drawerControls;

    return [...new Set(candidates)].filter(isVisiblyFocusable);
  };

  const focusDrawer = (type) => {
    const moveFocus = () => {
      if (!document.body.classList.contains(classes[type])) {
        return;
      }

      const drawer = document.querySelector(selectors[`${type}Drawer`]);
      const focusables = drawerFocusables(type);

      // Do not steal focus after the user has already moved within the drawer.
      if (focusables.includes(document.activeElement)) {
        return;
      }

      const target = [...(drawer?.querySelectorAll(focusableSelector) || [])]
        .find(isVisiblyFocusable)
        || focusables[0];

      target?.focus({ preventScroll: true });
    };

    window.requestAnimationFrame(moveFocus);

    // FacetWP/Shoptimizer can finish its filter-open handler after the first
    // animation frame and return focus to the trigger. Recheck once after that
    // bounded parent-theme work without disturbing focus already in the drawer.
    window.setTimeout(moveFocus, 80);
  };

  const containDrawerTab = (event) => {
    if ('Tab' !== event.key || window.innerWidth > 992) {
      return;
    }

    const openTypes = ['menu', 'filter'].filter((type) => (
      document.body.classList.contains(classes[type])
    ));

    if (!openTypes.length) {
      return;
    }

    const activeType = openTypes.find((type) => (
      drawerFocusables(type).includes(document.activeElement)
    ));
    const type = activeType || openTypes[openTypes.length - 1];
    const focusables = drawerFocusables(type);

    if (!focusables.length) {
      event.preventDefault();
      return;
    }

    const activeIndex = focusables.indexOf(document.activeElement);
    const leavesAtStart = event.shiftKey && activeIndex <= 0;
    const leavesAtEnd = !event.shiftKey && activeIndex === focusables.length - 1;

    if (-1 === activeIndex || leavesAtStart || leavesAtEnd) {
      event.preventDefault();
      focusables[
        event.shiftKey ? focusables.length - 1 : 0
      ].focus({ preventScroll: true });
    }
  };

  const syncState = () => {
    const isMobile = window.innerWidth <= 992;
    const body = document.body;
    const states = {
      cart: body.classList.contains(classes.cart),
      desktopSearch: Boolean(document.querySelector(`${selectors.searchDialog}[open]`)),
      filter: isMobile && body.classList.contains(classes.filter),
      menu: isMobile && body.classList.contains(classes.menu),
      mobileSearch: isMobile && body.classList.contains(classes.mobileSearch),
    };
    const cartDrawer = document.querySelector(selectors.cartDrawer);
    const mobileSearchDrawer = document.querySelector(selectors.mobileSearchDrawer);

    updateToggles(selectors.menu, states.menu, labels.menuOpen, labels.menuClose);
    updateToggles(selectors.filter, states.filter, labels.filterOpen, labels.filterClose);
    updateToggles(
      selectors.mobileSearch,
      states.mobileSearch,
      labels.searchOpen,
      labels.searchClose
    );
    updateToggles(
      selectors.desktopSearch,
      states.desktopSearch,
      labels.searchOpen,
      labels.searchClose
    );

    document.querySelectorAll(selectors.desktopSearch).forEach((trigger) => {
      trigger.setAttribute('aria-haspopup', 'dialog');
    });

    document.querySelectorAll(selectors.cart).forEach((trigger) => {
      if (cartDrawer) {
        trigger.setAttribute('aria-expanded', String(states.cart));
      } else {
        trigger.removeAttribute('aria-controls');
        trigger.removeAttribute('aria-expanded');
      }
    });

    setDrawerAvailability(
      document.querySelector(selectors.menuDrawer),
      isMobile && !states.menu,
      'menu'
    );
    setDrawerAvailability(
      document.querySelector(selectors.filterDrawer),
      isMobile && !states.filter,
      'filter'
    );
    setDrawerAvailability(
      mobileSearchDrawer,
      isMobile && Boolean(document.querySelector(selectors.mobileSearch)) && !states.mobileSearch,
      'mobileSearch'
    );
    setDrawerAvailability(cartDrawer, !states.cart, 'cart');

    if (states.menu && false === previous.menu) {
      focusDrawer('menu');
    }

    if (states.filter && false === previous.filter) {
      focusDrawer('filter');
    }

    if (isMobile && !states.menu && previous.menu) {
      focusReturnTarget('menu');
    }

    if (isMobile && !states.filter && previous.filter) {
      focusReturnTarget('filter');
    }

    if (states.mobileSearch && false === previous.mobileSearch) {
      mobileSearchDrawer
        ?.querySelector('input[type="search"]')
        ?.focus({ preventScroll: true });
    }

    if (states.cart && false === previous.cart) {
      cartDrawer?.focus({ preventScroll: true });
    }

    syncSubmenuToggles();
    Object.assign(previous, states);
  };

  const closeOnEscape = (event) => {
    if ('Escape' !== event.key) {
      return;
    }

    const searchDialog = document.querySelector(`${selectors.searchDialog}[open]`);
    const openType = searchDialog
      ? 'desktopSearch'
      : Object.keys(classes).find((type) => document.body.classList.contains(classes[type]));

    if (!openType) {
      return;
    }

    event.preventDefault();

    if (searchDialog) {
      searchDialog.close();
    } else {
      document.body.classList.remove(classes[openType]);
    }

    focusReturnTarget(openType);
    syncState();
  };

  const init = () => {
    decorateControls();
    syncState();

    document.addEventListener('focusin', (event) => {
      const drawer = closest(
        event.target,
        `${selectors.menuDrawer}, ${selectors.filterDrawer}, `
          + `${selectors.mobileSearchDrawer}, ${selectors.cartDrawer}, `
          + `${selectors.searchDialog}, ${selectors.menuClose}`
      );

      if (!drawer) {
        lastExternalFocus = event.target;
      }
    });

    document.addEventListener(
      'click',
      (event) => {
        const triggerTypes = ['menu', 'filter', 'mobileSearch', 'cart', 'desktopSearch'];
        const type = triggerTypes.find((triggerType) => (
          closest(event.target, selectors[triggerType])
        ));

        if (type) {
          lastTriggers[type] = closest(event.target, selectors[type]);
        }

        const mobileSearchToggle = closest(event.target, selectors.mobileSearch);
        const desktopSearchToggle = closest(event.target, selectors.desktopSearch);

        if (desktopSearchToggle) {
          window.setTimeout(() => {
            promoteSearchDialog();
            syncState();
          }, 0);
        } else if (mobileSearchToggle) {
          const wasOpen = document.body.classList.contains(classes.mobileSearch);

          window.setTimeout(() => {
            // Supply only the missing toggle when an optimisation layer skipped parent JS.
            if (document.body.classList.contains(classes.mobileSearch) === wasOpen) {
              document.body.classList.toggle(classes.mobileSearch);
            }

            syncState();
          }, 0);
        } else {
          window.setTimeout(syncState, 0);
        }
      },
      true
    );

    document.addEventListener('keydown', (event) => {
      const filterClose = closest(event.target, `${selectors.filterDrawer} .filters.close-drawer`);

      if (filterClose && ('Enter' === event.key || ' ' === event.key)) {
        event.preventDefault();
        filterClose.click();
      }
    });
    document.addEventListener('keydown', containDrawerTab, true);
    document.addEventListener('keydown', closeOnEscape, true);

    new MutationObserver(syncState).observe(document.body, {
      attributeFilter: ['class'],
      attributes: true,
    });

    const searchDialog = document.querySelector(selectors.searchDialog);

    if (searchDialog) {
      new MutationObserver(syncState).observe(searchDialog, {
        attributeFilter: ['open'],
        attributes: true,
      });
    }

    new MutationObserver(() => {
      decorateControls();
      syncState();
    }).observe(document.body, {
      childList: true,
      subtree: true,
    });

    window.addEventListener('resize', () => {
      if (window.innerWidth > 992) {
        document.body.classList.remove(
          classes.menu,
          classes.filter,
          classes.mobileSearch
        );
      }

      syncState();
    });
  };

  if ('loading' === document.readyState) {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
